import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { StarRounded, StarOutlineRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { shellTokens } from './shellTokens';
import { useShell } from './ShellContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganizations } from '../../contexts/OrganizationContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { BUILD_VERSION, BUILD_HASH } from '../../version';
import { useOverlayFocus } from './useOverlayFocus';
import { shellViewAsShortcut } from './shellShortcut';

const { color, radius, shadow, motion, z } = shellTokens;

interface ProfileMenuProps {
  name: string;
  email: string;
  role: string;
  /** Only platform admins may impersonate. */
  canViewAs: boolean;
}

interface MenuRowProps {
  label: string;
  meta?: string;
  danger?: boolean;
  onClick: () => void;
  /** A control that lives to the right of `meta`, e.g. a default-org toggle. */
  trailing?: React.ReactNode;
}

const MenuRow: React.FC<MenuRowProps> = ({ label, meta, danger, onClick, trailing }) => (
  <ButtonBase
    role="menuitem"
    // Roving tabindex: the hook moves focus between items with the arrow keys.
    tabIndex={-1}
    onClick={onClick}
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      p: '7px 10px',
      borderRadius: radius.menuRow,
      textAlign: 'left',
      '&:hover': { background: color.lineSubtle },
    }}
  >
    <Typography
      noWrap
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: '13px',
        fontWeight: 500,
        color: danger ? color.dangerDeep : color.body,
      }}
    >
      {label}
    </Typography>
    {meta && (
      <Typography sx={{ fontSize: '11.5px', color: color.faint, flexShrink: 0 }}>{meta}</Typography>
    )}
    {trailing}
  </ButtonBase>
);

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ name, email, role, canViewAs }) => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { overlay, closeOverlay, setOverlay } = useShell();
  const { logout } = useAuth();
  const { organizations, actingOrganizationId, canSwitch, switchOrganization } = useOrganizations();
  const { language, region, supportedLanguages } = useLocale();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  // Which organization a person lands in by default, next time they sign in.
  // A group-granted store (`via: 'group'`) is not a real membership and
  // cannot be set as the default — the API would refuse it.
  const setDefaultOrganization = useMutation({
    mutationFn: async (organizationId: string) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/session/primary-organization', {
        method: 'PUT',
        headers,
        body: JSON.stringify({ organizationId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || body?.message || t('shell.profile.setDefaultFailed'));
      }
    },
    onSuccess: () => {
      showToast(t('shell.profile.setDefaultSuccess'), 'success');
      queryClient.invalidateQueries({ queryKey: ['session-organizations'] });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  const { containerRef, onKeyDown } = useOverlayFocus<HTMLDivElement>(overlay?.kind === 'profile', {
    menu: true,
  });

  /**
   * The caller's own organizations first, then one section per managed group
   * holding the member stores that group reaches. A person with no group sees
   * exactly the single, unheaded section they saw before.
   */
  const switcherSections = useMemo(() => {
    const own = organizations.filter((organization) => organization.via !== 'group');
    const sections: Array<{
      key: string;
      heading: string | null;
      organizations: typeof organizations;
    }> = own.length > 0 ? [{ key: 'own', heading: null, organizations: own }] : [];

    const byGroup = new Map<string, typeof organizations>();
    organizations
      .filter((organization) => organization.via === 'group' && organization.groupId)
      .forEach((organization) => {
        const key = organization.groupId as string;
        byGroup.set(key, [...(byGroup.get(key) || []), organization]);
      });
    byGroup.forEach((members, groupId) => {
      sections.push({ key: groupId, heading: members[0].groupName || null, organizations: members });
    });
    return sections;
  }, [organizations]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), motion.copyFlashMs);
    return () => clearTimeout(timer);
  }, [copied]);

  if (overlay?.kind !== 'profile') return null;

  const versionString = t('shell.profile.version', {
    version: BUILD_VERSION,
    hash: BUILD_HASH,
  });

  const go = (path: string) => {
    closeOverlay();
    navigate(path);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(versionString);
      setCopied(true);
    } catch {
      // Clipboard is blocked in some embedded contexts; the string stays selectable.
    }
  };

  const openRegionMenu = () => {
    // The region menu belongs under its own chip in the header. On narrow
    // viewports that chip is `display: none` — still in the DOM, so
    // getBoundingClientRect() returns a zero rect rather than null, and
    // treating that as an anchor puts the menu a viewport-width off-screen.
    const chip = document.getElementById('shell-region-chip');
    const rect = chip?.getBoundingClientRect();
    const chipVisible = Boolean(rect && rect.width > 0 && rect.height > 0);
    setOverlay({
      kind: 'region',
      at: chipVisible && rect
        ? { top: rect.bottom + 8, right: window.innerWidth - rect.right }
        : { top: shellTokens.size.header + 8, right: 16 },
    });
  };

  const handleSignOut = async () => {
    closeOverlay();
    await logout();
    navigate('/');
  };

  return (
    <>
      <Box
        onClick={closeOverlay}
        sx={{ position: 'fixed', inset: 0, zIndex: z.menu - 1 }}
        aria-hidden
      />
      <Box
        ref={containerRef}
        role="menu"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        aria-label={t('shell.profile.open')}
        sx={{
          position: 'fixed',
          left: overlay.at.left,
          bottom: overlay.at.bottom,
          width: 250,
          maxWidth: 'calc(100vw - 24px)',
          // Bottom-anchored above the sidebar trigger: on a short viewport the
          // header, five rows and version footer are taller than the space
          // above it, and the top actions would render off-screen.
          maxHeight: `calc(100vh - ${overlay.at.bottom + 12}px)`,
          overflowY: 'auto',
          zIndex: z.menu,
          background: color.white,
          border: `1px solid ${color.line}`,
          borderRadius: radius.menu,
          boxShadow: shadow.profileMenu,
          p: '6px',
        }}
      >
        <Box sx={{ p: '8px 10px 10px', borderBottom: `1px solid ${color.lineSubtle}`, mb: '4px' }}>
          <Typography noWrap sx={{ fontSize: '13.5px', fontWeight: 600, color: color.ink }}>
            {name}
          </Typography>
          <Typography noWrap sx={{ fontSize: '12px', color: color.muted }}>
            {email}
          </Typography>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              mt: '6px',
              p: '2px 8px',
              borderRadius: radius.pill,
              background: color.lineSubtle,
              color: color.bodyAlt,
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            {role}
          </Box>
        </Box>

        {/* Only for the few people who belong to more than one organization:
            a chain's regional manager, a buying group's operator. Everyone
            else never sees this section. */}
        {canSwitch && (
          <Box sx={{ mb: '4px', pb: '4px', borderBottom: `1px solid ${color.lineSubtle}` }}>
            <Typography
              sx={{ p: '6px 10px 2px', fontSize: '11px', fontWeight: 600, color: color.faint }}
            >
              {t('shell.profile.organizations')}
            </Typography>
            {switcherSections.map((section) => (
              <Box key={section.key}>
                {/* A store reached through a managed group is listed under
                    that group's name, so it is clear whose store it is. */}
                {section.heading && (
                  <Typography
                    sx={{ p: '4px 10px 2px', fontSize: '11px', fontWeight: 600, color: color.faint }}
                  >
                    {section.heading}
                  </Typography>
                )}
                {section.organizations.map((organization) => (
                  <MenuRow
                    key={organization.id}
                    label={organization.name}
                    meta={
                      organization.id === actingOrganizationId
                        ? t('shell.profile.currentOrganization')
                        : undefined
                    }
                    onClick={() => {
                      closeOverlay();
                      switchOrganization(organization.id);
                    }}
                    trailing={
                      organization.via === 'group' ? undefined : (
                        <Box
                          component="span"
                          role="button"
                          tabIndex={-1}
                          aria-label={
                            organization.isPrimary
                              ? t('shell.profile.defaultOrganization')
                              : t('shell.profile.setAsDefault')
                          }
                          title={
                            organization.isPrimary
                              ? t('shell.profile.defaultOrganization')
                              : t('shell.profile.setAsDefault')
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if (organization.isPrimary || setDefaultOrganization.isPending) return;
                            setDefaultOrganization.mutate(organization.id);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            cursor: organization.isPrimary ? 'default' : 'pointer',
                            p: '2px',
                            borderRadius: radius.keyHint,
                            '&:hover': organization.isPrimary
                              ? undefined
                              : { background: color.lineSubtle },
                          }}
                        >
                          {organization.isPrimary ? (
                            <StarRounded sx={{ fontSize: 15, color: color.accent }} />
                          ) : (
                            <StarOutlineRounded sx={{ fontSize: 15, color: color.faint }} />
                          )}
                        </Box>
                      )
                    }
                  />
                ))}
              </Box>
            ))}
          </Box>
        )}

        <MenuRow label={t('shell.profile.preferences')} onClick={() => go('/app/profile')} />
        <MenuRow
          label={t('shell.profile.languageRegion')}
          meta={`${supportedLanguages[language].nativeName} (${region})`}
          onClick={openRegionMenu}
        />
        <MenuRow
          label={t('shell.profile.notificationSettings')}
          onClick={() => go('/app/profile#notification-preferences')}
        />
        {canViewAs && (
          <MenuRow
            label={t('shell.viewAs.menuItem')}
            meta={shellViewAsShortcut()}
            onClick={() => setOverlay({ kind: 'palette', mode: 'as' })}
          />
        )}
        <MenuRow label={t('shell.profile.signOut')} danger onClick={handleSignOut} />

        <Box
          sx={{
            mt: '4px',
            pt: '8px',
            px: '10px',
            pb: '4px',
            borderTop: `1px solid ${color.lineSubtle}`,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {/* Informational, not a menu item — the build sha is what support asks for. */}
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '11.5px', color: color.muted }}>
            {versionString}
          </Typography>
          <ButtonBase
            onClick={handleCopy}
            aria-label={`${t('shell.profile.copy')} ${versionString}`}
            sx={{
              fontSize: '11.5px',
              fontWeight: 600,
              color: copied ? color.success : color.bodyAlt,
              borderRadius: radius.keyHint,
              px: '4px',
            }}
          >
            {copied ? t('shell.profile.copied') : t('shell.profile.copy')}
          </ButtonBase>
        </Box>
      </Box>
    </>
  );
};
